import { useState, useEffect } from "react";
import { Link } from "wouter";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Instagram,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  Users,
  UtensilsCrossed,
  Check,
} from "lucide-react";
import { RESTAURANT_RECEIPT_BRAND } from "@/lib/restaurant-brand";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BRAND = {
  name: "Butt Karahi Tikka",
  tagline: "City Housing, Sialkot",
  address:
    "53, C Block, near Water Fountains, Citi Housing Society Sialkot, 51310, Pakistan",
  phone: RESTAURANT_RECEIPT_BRAND.contact,
  email: "buttkarahitikkacitihousing@gmail.com",
  hours: "Daily · 10:00 AM – 2:00 AM",
  mapQuery: encodeURIComponent(
    "Plaza 53 Fountain Chowk City Housing Sialkot Pakistan",
  ),
};

const img = (file: string) => `/${file}`;

const HERO_IMAGES = ["1.jpeg", "2.jpeg", "3.jpeg", "4.jpeg"].map(img);

const ABOUT_IMAGES = ["5.jpeg", "6.jpeg", "7.jpeg", "8.jpeg"].map(img);

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "events", label: "Event Gallery" },
  { id: "contact", label: "Contact" },
];

const EVENT_GALLERY = [
  {
    title: "Birthdays & Celebrations",
    subtitle: "Memorable milestones with authentic flavor",
    description:
      "Mark your special day with sizzling karahi, fresh naan, and a warm family atmosphere.",
    details: [
      "Whether it is a child's birthday, anniversary, or a surprise party for someone special, we create a welcoming setup with generous food and attentive service.",
      "Choose from our signature karahi, handi specials, BBQ platters, and custom dessert arrangements. We help you plan seating, timing, and menu portions for your guest count.",
      "Our team handles the dining flow so you can focus on celebrating — from welcome drinks to the final cake cutting.",
    ],
    highlights: [
      "Custom menu for 10–80 guests",
      "Balloon & table décor coordination",
      "Kids-friendly platters available",
      "Advance booking recommended",
    ],
    capacity: "10 – 80 guests",
    images: [
      img("9.jpeg"),
      img("10.jpeg"),
      img("11.jpeg"),
      img("12.jpeg"),
      img("13.jpeg"),
    ],
    imageCaptions: [
      "Family birthday dinner setup",
      "Celebration platters & desserts",
      "Evening party atmosphere",
      "Group seating arrangement",
      "Special occasion décor",
    ],
  },
  {
    title: "Corporate & Team Dinners",
    subtitle: "Professional hosting for teams & clients",
    description:
      "Host colleagues and clients with generous platters, quick service, and flexible seating.",
    details: [
      "Impress your team or business guests with a polished dine-in experience at Butt Karahi Tikka. We offer structured service windows for corporate lunches and after-office dinners.",
      "From quick turnaround platters to multi-course Pakistani spreads, menus can be tailored for dietary preferences and budget tiers.",
      "Ideal for quarterly reviews, farewell dinners, client entertainment, and department celebrations in City Housing, Sialkot.",
    ],
    highlights: [
      "Dedicated service for business groups",
      "Invoice-friendly billing options",
      "Private section on request",
      "Karahi, BBQ & rice platters",
    ],
    capacity: "15 – 100 guests",
    images: [img("14.jpeg"), img("1.jpeg"), img("2.jpeg"), img("3.jpeg")],
    imageCaptions: [
      "Corporate dinner gathering",
      "Fine dining presentation",
      "Team lunch service",
      "Restaurant private section",
    ],
  },
  {
    title: "Family Gatherings",
    subtitle: "Weekend lunches & large family dinners",
    description:
      "From weekend lunches to large family dinners — share authentic flavors together.",
    details: [
      "Bring the whole family together over slow-cooked karahi, fresh naan, and classic desi sides served hot from our kitchen.",
      "We accommodate multi-generational groups with comfortable seating, shareable platters, and flexible timing for late-evening gatherings.",
      "Popular for Eid meals, Sunday brunch-style lunches, university break reunions, and relatives visiting from out of town.",
    ],
    highlights: [
      "Shareable karahi & handi menus",
      "Dine-in, takeaway & pre-order",
      "Spacious family seating",
      "Open daily 10 AM – 2 AM",
    ],
    capacity: "8 – 60 guests",
    images: [
      img("4.jpeg"),
      img("5.jpeg"),
      img("6.jpeg"),
      img("7.jpeg"),
      img("8.jpeg"),
    ],
    imageCaptions: [
      "Large family table setup",
      "Weekend lunch crowd",
      "Signature karahi service",
      "Traditional desi spread",
      "Group dining experience",
    ],
  },
];

type EventGalleryItem = (typeof EVENT_GALLERY)[number];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

function whatsAppHref(phone: string) {
  const digits = phone.replace(/\D/g, "");
  const intl = digits.startsWith("92")
    ? digits
    : `92${digits.replace(/^0/, "")}`;
  return `https://wa.me/${intl}`;
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [heroSlide, setHeroSlide] = useState(0);
  const [aboutSlide, setAboutSlide] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<EventGalleryItem | null>(
    null,
  );
  const [eventModalSlide, setEventModalSlide] = useState(0);

  useEffect(() => {
    setEventModalSlide(0);
  }, [selectedEvent]);

  useEffect(() => {
    document.documentElement.classList.add("no-scrollbar");
    document.body.classList.add("no-scrollbar");
    return () => {
      document.documentElement.classList.remove("no-scrollbar");
      document.body.classList.remove("no-scrollbar");
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAboutSlide((prev) => (prev + 1) % ABOUT_IMAGES.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, []);

  const menuIconClass = scrolled ? "text-black" : "text-black";

  return (
    <div className="min-h-screen bg-white text-stone-900 font-[family-name:var(--font-sans)]">
      {/* ── Header ── */}
      <header className="fixed inset-x-0 top-0 z-50 h-[70px] w-full border-b border-white/40 bg-white/55 shadow-sm backdrop-blur-lg backdrop-saturate-150 transition-all duration-300">
        <div className="flex w-full h-full items-center gap-4 px-6 lg:gap-8 lg:px-12 xl:px-16">
          {/* Logo — left */}
          <button
            type="button"
            onClick={() => scrollTo("home")}
            className="group shrink-0"
          >
            <div className="flex items-center justify-center h-24 w-24 rounded-full p-1">
              <img
                src="/logo.png"
                alt={BRAND.name}
                className="h-full w-full rounded-full object-contain"
              />
            </div>
          </button>

          {/* Nav — right */}
          <div className="flex flex-1 items-center justify-end gap-2 sm:gap-3">
            <nav className="hidden items-center gap-6 lg:gap-8 md:flex">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className={`group relative text-base font-semibold tracking-wide transition lg:text-lg text-black hover:text-primary`}
                >
                  {item.label}
                  <span className="absolute -bottom-1.5 left-1/2 h-0.5 w-0 -translate-x-1/2 rounded-full bg-primary transition-all group-hover:w-full" />
                </button>
              ))}
              <Link
                href="/login"
                className="ml-1 bg-primary px-6 py-1.5 text-base font-semibold text-white transition hover:bg-primary/90 lg:text-lg"
              >
                Login
              </Link>
            </nav>

            <button
              type="button"
              className={`rounded-lg p-2 transition hover:text-primary md:hidden ${menuIconClass}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <X className="h-7 w-7" />
              ) : (
                <Menu className="h-7 w-7" />
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-white/50 bg-white/65 px-6 py-4 backdrop-blur-lg backdrop-saturate-150 md:hidden">
            <div className="flex flex-col gap-1">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    scrollTo(item.id);
                    setMenuOpen(false);
                  }}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-stone-700 hover:bg-primary/5 hover:text-primary"
                >
                  {item.label}
                </button>
              ))}
              <Link
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="mt-2 bg-primary px-6 py-1.5 text-center text-base font-semibold text-white transition hover:bg-primary/90"
              >
                Login
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section
        id="home"
        className="relative flex min-h-[92vh] w-full items-center overflow-hidden"
      >
        {HERO_IMAGES.map((src, index) => (
          <div
            key={src}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
              index === heroSlide ? "opacity-100" : "opacity-0"
            }`}
            style={{ backgroundImage: `url(${src})` }}
            aria-hidden={index !== heroSlide}
          />
        ))}

        <div className="relative z-10 flex w-full items-center px-6 py-32 pt-36 sm:px-10 lg:px-16 xl:px-20 lg:py-40 lg:pt-44">
          <div className="w-full max-w-5xl">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.3em] text-primary drop-shadow-sm sm:text-base">
              Authentic Pakistani Cuisine
            </p>
            <h1
              className="font-serif text-4xl font-bold leading-[1.1] text-white drop-shadow-lg sm:text-5xl md:text-6xl lg:text-7xl xl:text-[4.25rem]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Where Every Meal Becomes a{" "}
              <span className="text-primary">Memorable</span> Experience
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/95 drop-shadow-md sm:text-xl lg:text-2xl">
              Sizzling karahi, handi specialties, and classic desi flavors —
              served fresh in the heart of City Housing, Sialkot.
            </p>
            <div className="mt-10">
              <button
                type="button"
                onClick={() => scrollTo("contact")}
                className="inline-flex items-center gap-2 bg-primary px-8 py-4 text-base font-semibold text-white shadow-xl transition hover:bg-primary/90 sm:text-lg"
              >
                Visit Us
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Carousel dots */}
        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {HERO_IMAGES.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => setHeroSlide(index)}
              className={`h-2.5 rounded-full transition-all ${
                index === heroSlide
                  ? "w-8 bg-primary"
                  : "w-2.5 bg-white/70 hover:bg-white"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid w-full max-w-screen-2xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-12 lg:px-10">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-xl shadow-stone-200/80">
            {ABOUT_IMAGES.map((src, index) => (
              <img
                key={src}
                src={src}
                alt={`Butt Karahi Tikka gallery ${index + 1}`}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
                  index === aboutSlide ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
            <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-stone-200" />
            <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
              {ABOUT_IMAGES.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`About slide ${index + 1}`}
                  onClick={() => setAboutSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === aboutSlide
                      ? "w-7 bg-primary"
                      : "w-2 bg-white/80 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              About Us
            </p>
            <h2
              className="mt-2 font-serif text-3xl font-bold text-stone-900 sm:text-4xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              A Taste of Tradition in Sialkot
            </h2>
            <p className="mt-5 text-base leading-relaxed text-stone-600">
              {BRAND.name} brings the true spirit of Pakistani hospitality to
              Fountain Chowk, City Housing. Our kitchen is built around
              slow-cooked karahi, aromatic handi dishes, and freshly prepared
              sides — made with quality ingredients and served with pride.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">
              Whether you are stopping by for a quick lunch, bringing the family
              for dinner, or booking a celebration, our team is here to make
              every visit warm, flavorful, and unforgettable.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Signature desi murgh & mutton karahi",
                "Dine-in, take-away & delivery",
                "Events, gatherings & catering",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-sm font-medium text-stone-700"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Event Gallery ── */}
      <section
        id="events"
        className="border-y border-stone-100 bg-stone-50 py-20 sm:py-28"
      >
        <div className="mx-auto w-full max-w-screen-2xl px-5 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Event Gallery
            </p>
            <h2
              className="mt-2 font-serif text-3xl font-bold text-stone-900 sm:text-4xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Celebrate With Us
            </h2>
            <p className="mt-4 text-stone-600">
              From intimate family dinners to large gatherings — browse moments
              we help create with authentic food and reliable service.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {EVENT_GALLERY.map((event) => (
              <button
                key={event.title}
                type="button"
                onClick={() => setSelectedEvent(event)}
                className="group overflow-hidden rounded-2xl border border-stone-200 bg-white text-left shadow-sm transition hover:border-primary/30 hover:shadow-md"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={event.images[0]}
                    alt={event.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-transparent to-transparent opacity-80" />
                  <h3 className="absolute bottom-4 left-4 right-4 text-lg font-bold text-white drop-shadow-md">
                    {event.title}
                  </h3>
                </div>
                <p className="p-5 text-sm leading-relaxed text-stone-600">
                  {event.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Dialog
        open={selectedEvent !== null}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      >
        <DialogContent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-5xl gap-0 overflow-hidden border-stone-200 p-0 sm:rounded-2xl lg:max-w-5xl">
          {selectedEvent && (
            <div className="max-h-[92vh] overflow-y-auto no-scrollbar">
              {/* Hero image */}
              <div className="relative aspect-[16/9] bg-stone-100">
                <img
                  src={selectedEvent.images[eventModalSlide]}
                  alt={`${selectedEvent.title} ${eventModalSlide + 1}`}
                  className="h-full w-full object-cover"
                />
                {selectedEvent.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setEventModalSlide(
                          (prev) =>
                            (prev - 1 + selectedEvent.images.length) %
                            selectedEvent.images.length,
                        )
                      }
                      className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-md transition hover:bg-white"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setEventModalSlide(
                          (prev) => (prev + 1) % selectedEvent.images.length,
                        )
                      }
                      className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-800 shadow-md transition hover:bg-white"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
                <div className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                  {eventModalSlide + 1} / {selectedEvent.images.length}
                </div>
              </div>

              {/* Thumbnails */}
              {selectedEvent.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2 border-b border-stone-100 bg-white p-3 sm:grid-cols-5">
                  {selectedEvent.images.map((src, index) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setEventModalSlide(index)}
                      className={`aspect-[4/3] overflow-hidden rounded-lg border-2 transition ${
                        index === eventModalSlide
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={src}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Content */}
              <div className="space-y-5 bg-white p-5 sm:p-6">
                <DialogHeader className="space-y-1.5 text-left">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                    Event Gallery
                  </p>
                  <DialogTitle
                    className="font-serif text-xl text-stone-900 sm:text-2xl"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {selectedEvent.title}
                  </DialogTitle>
                  <p className="text-sm text-stone-500">
                    {selectedEvent.subtitle}
                  </p>
                  <DialogDescription className="text-sm leading-relaxed text-stone-600">
                    {selectedEvent.description}
                  </DialogDescription>
                </DialogHeader>

                <p className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600">
                  {selectedEvent.imageCaptions[eventModalSlide]}
                </p>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    <Users className="h-3.5 w-3.5" />
                    {selectedEvent.capacity}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700">
                    <UtensilsCrossed className="h-3.5 w-3.5" />
                    {selectedEvent.images.length} photos
                  </span>
                </div>

                <div className="grid gap-5 border-t border-stone-100 pt-5 lg:grid-cols-2 pb-5">
                  <div className="space-y-2.5">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900">
                      About this event
                    </h4>
                    {selectedEvent.details.map((paragraph) => (
                      <p
                        key={paragraph.slice(0, 40)}
                        className="text-sm leading-relaxed text-stone-600"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <div className="space-y-2.5">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-stone-900">
                      What we offer
                    </h4>
                    <ul className="space-y-2">
                      {selectedEvent.highlights.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-sm text-stone-700"
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Check className="h-2.5 w-2.5" />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Contact ── */}
      <section id="contact" className="bg-slate-100 py-10 sm:py-20">
        <div className="mx-auto w-full px-5 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Contact Us
            </p>
            <h2
              className="mt-2 font-serif text-3xl font-bold text-stone-900 sm:text-4xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Visit or Get in Touch
            </h2>
            <p className="mt-4 text-stone-600">
              Visit us at Fountain Chowk, call for reservations, or message us
              for events and catering — we would love to hear from you.
            </p>
          </div>
          <div className="mt-12 grid gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="space-y-6">
              <div className="flex gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Address</p>
                  <p className="mt-1 text-sm text-stone-600">{BRAND.address}</p>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Phone</p>
                  <a
                    href={`tel:${BRAND.phone}`}
                    className="mt-1 block text-sm text-primary hover:underline"
                  >
                    {BRAND.phone}
                  </a>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Email</p>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="mt-1 block text-sm text-primary hover:underline"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </div>
              <div className="flex gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">Hours</p>
                  <p className="mt-1 text-sm text-stone-600">{BRAND.hours}</p>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-stone-200 shadow-md shadow-stone-200/60">
              <iframe
                title="Butt Karahi Tikka location on Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107784.8898804583!2d74.33368454335937!3d32.41171579999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391ec3fe2d054a03%3A0x100b825605e60193!2sButt%20Karahi%20Tikka%20Citi%20Housing%20Sialkot!5e0!3m2!1sen!2s!4v1788257228529!5m2!1sen!2s"
                className="h-[320px] w-full sm:h-[400px] lg:h-full min-h-[320px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-stone-800 bg-stone-900 text-stone-300">
        <div className="mx-auto w-full max-w-screen px-5 py-14 sm:px-8 lg:px-10">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-center h-24 w-24">
                <img
                  src="/footerLogo.png"
                  alt={BRAND.name}
                  className="h-full w-full object-contain"
                />
              </div>
              <p className="mt-4 text-lg font-bold text-white">{BRAND.name}</p>
              <p className="mt-1 text-sm text-stone-400">{BRAND.tagline}</p>
              <p className="mt-3 text-sm leading-relaxed text-stone-400">
                {BRAND.address}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Quick Links
              </p>
              <ul className="mt-4 space-y-2">
                {NAV.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollTo(item.id)}
                      className="text-sm transition hover:text-primary"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
                <li>
                  <Link href="/login" className="text-sm hover:text-primary">
                    Login
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Contact
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                <li>
                  <a href={`tel:${BRAND.phone}`} className="hover:text-primary">
                    {BRAND.phone}
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="hover:text-primary"
                  >
                    {BRAND.email}
                  </a>
                </li>
                <li className="text-stone-400">{BRAND.hours}</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Follow Us
              </p>
              <div className="mt-4 flex gap-3">
                {[
                  { Icon: Facebook, label: "Facebook", href: "#" },
                  {
                    Icon: Instagram,
                    label: "Instagram",
                    href: "https://www.instagram.com/buttkarahitikkacitihousing/",
                  },
                ].map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-600 text-stone-300 transition hover:border-primary hover:bg-primary hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-stone-800 pt-8 text-xs text-stone-500 sm:flex-row">
            <p>
              © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            </p>
            <p>
              Powered by{" "}
              <a
                href="https://smartaisoft.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-400 transition hover:text-primary"
              >
                {RESTAURANT_RECEIPT_BRAND.poweredBy}
              </a>
            </p>
          </div>
        </div>
      </footer>

      <a
        href={whatsAppHref(BRAND.phone)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Chat on WhatsApp ${BRAND.phone}`}
        className="fixed bottom-20 right-5 z-40 transition hover:scale-105"
      >
        <img
          src="/wattsapp.jpg"
          alt="WhatsApp"
          className="h-12 w-12 shrink-0 rounded-full object-cover sm:h-16 sm:w-16"
        />
      </a>
    </div>
  );
}
